import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
} from '@chronosphereio/chrono-ui';
import { Creation as SparkleIcon } from 'mdi-material-ui';
import type { FieldGenerateButtonProps } from '@/plugin-system';

export interface FieldWithGenerateButtonProps extends Omit<TextFieldProps, 'label' | 'onChange' | 'sx' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  label: string;
  generate?: FieldGenerateButtonProps;
}

/**
 * Renders a text field with an explicit Generate / Regenerate button and optional Undo.
 * Alternative to FieldWithSuggestion for button-triggered AI generation flows.
 */
export function FieldWithGenerateButton({
  value,
  onChange,
  label,
  generate,
  InputProps,
  ...textFieldProps
}: FieldWithGenerateButtonProps) {
  if (!generate) {
    return (
      <TextField
        {...textFieldProps}
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputProps={InputProps}
      />
    );
  }

  const { onGenerate, isGenerating, hasGenerated, errorMessage, onUndo } = generate;
  const buttonLabel = hasGenerated ? 'Regenerate' : 'Generate';

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <TextField
          {...textFieldProps}
          fullWidth
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          InputProps={{
            ...InputProps,
            endAdornment: (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginY: -0.25,
                  marginRight: -1,
                }}
              >
                <Button
                  variant="tertiary"
                  size="small"
                  disabled={isGenerating}
                  onClick={onGenerate}
                  StartIcon={isGenerating ? <CircularProgress size={14} /> : <SparkleIcon fontSize="small" />}
                >
                  {isGenerating ? 'Generating...' : buttonLabel}
                </Button>
                {onUndo && (
                  <Button variant="tertiary" size="small" disabled={isGenerating} onClick={onUndo}>
                    Undo
                  </Button>
                )}
              </Stack>
            ),
          }}
        />
      </Box>
      {errorMessage && (
        <Box sx={{ pt: '1px', mb: -1, height: '1.2em' }}>
          <Typography
            component="div"
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.2,
              m: 0,
              verticalAlign: 'baseline',
            }}
          >
            {errorMessage}{' '}
            <Link
              component="button"
              type="button"
              variant="caption"
              aria-disabled={isGenerating}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isGenerating) {
                  onGenerate();
                }
              }}
              sx={{
                verticalAlign: 'baseline',
                textDecoration: 'underline',
                ...(isGenerating ? { opacity: 0.5, pointerEvents: 'none' } : {}),
              }}
            >
              Try Again
            </Link>
          </Typography>
        </Box>
      )}
    </>
  );
}
